import { useState } from "react";
import { useAuth } from "../components/AuthContext";
import { bootstrap, createUser } from "../services/auth";
import { configureDb } from "../services/db";
import type { PortalRole } from "../types";

export default function Login() {
  const { login, error, loading } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [showSetup, setShowSetup] = useState(false);

  // Setup form state
  const [setupCreds, setSetupCreds] = useState({ accessKeyId: "", secretAccessKey: "" });
  const [setupUser, setSetupUser] = useState({
    username: "",
    password: "",
    displayName: "",
    email: "",
    role: "admin" as PortalRole,
  });
  const [setupStep, setSetupStep] = useState<"creds" | "user">("creds");
  const [setupDone, setSetupDone] = useState(false);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    try {
      await login(username, password);
    } catch (err: any) {
      setLocalError(err.message);
    }
  }

  async function handleSetupCreds(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    try {
      // Bootstrap DB access with these credentials
      bootstrap(setupCreds);
      configureDb(setupCreds);
      setSetupStep("user");
    } catch (err: any) {
      setLocalError(err.message);
    }
  }

  async function handleCreateFirstUser(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    try {
      await createUser({
        username: setupUser.username,
        password: setupUser.password,
        role: setupUser.role,
        displayName: setupUser.displayName,
        email: setupUser.email,
      });
      setSetupDone(true);
      setShowSetup(false);
      // Pre-fill login
      setUsername(setupUser.username);
      setPassword("");
    } catch (err: any) {
      setLocalError(err.message);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>POS DevPortal</h1>
          <p>Sign in to manage deployments, versions, and pipeline.</p>
        </div>

        {(error || localError) && (
          <div className="error-banner" style={{ marginBottom: "1rem" }}>
            ⚠️ {error || localError}
          </div>
        )}

        {setupDone && (
          <div style={{ background: "var(--success)", color: "white", padding: "0.75rem", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.85rem" }}>
            ✓ Admin user created. Sign in with your new credentials.
          </div>
        )}

        {!showSetup ? (
          <>
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  required
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>

              <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <div className="login-footer">
              <button
                className="setup-link"
                onClick={() => setShowSetup(true)}
                style={{ background: "none", border: "none", color: "var(--primary)", cursor: "pointer", fontSize: "0.8rem", marginTop: "1rem" }}
              >
                First time? Set up admin account →
              </button>
            </div>
          </>
        ) : (
          <div className="setup-flow">
            {setupStep === "creds" ? (
              <>
                <h3 style={{ marginBottom: "0.5rem" }}>Step 1: Connect to AWS</h3>
                <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "1rem" }}>
                  Enter IAM credentials with DynamoDB access in the Tooling account.
                  These are used one time to set up the users table.
                </p>
                <form onSubmit={handleSetupCreds} className="login-form">
                  <div className="form-group">
                    <label>Access Key ID</label>
                    <input
                      type="text"
                      value={setupCreds.accessKeyId}
                      onChange={(e) => setSetupCreds({ ...setupCreds, accessKeyId: e.target.value })}
                      placeholder="AKIA..."
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Secret Access Key</label>
                    <input
                      type="password"
                      value={setupCreds.secretAccessKey}
                      onChange={(e) => setSetupCreds({ ...setupCreds, secretAccessKey: e.target.value })}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary login-btn">Next →</button>
                </form>
              </>
            ) : (
              <>
                <h3 style={{ marginBottom: "0.5rem" }}>Step 2: Create Admin User</h3>
                <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "1rem" }}>
                  Set up your portal login and link your IAM credentials.
                </p>
                <form onSubmit={handleCreateFirstUser} className="login-form">
                  <div className="form-group">
                    <label>Display Name</label>
                    <input
                      type="text"
                      value={setupUser.displayName}
                      onChange={(e) => setSetupUser({ ...setupUser, displayName: e.target.value })}
                      placeholder="Randy Marquez"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      value={setupUser.username}
                      onChange={(e) => setSetupUser({ ...setupUser, username: e.target.value })}
                      placeholder="randy"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={setupUser.password}
                      onChange={(e) => setSetupUser({ ...setupUser, password: e.target.value })}
                      placeholder="Choose a strong password"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={setupUser.email}
                      onChange={(e) => setSetupUser({ ...setupUser, email: e.target.value })}
                      placeholder="randy@company.com"
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary login-btn">Create Admin & Finish</button>
                  <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.75rem" }}>
                    An IAM user and access keys will be created automatically in the Tooling account.
                  </p>
                </form>
              </>
            )}

            <button
              onClick={() => { setShowSetup(false); setSetupStep("creds"); setLocalError(null); }}
              style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "0.8rem", marginTop: "1rem" }}
            >
              ← Back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
